import { z } from "zod";

/**
 * Caps on the ship-gate refusal's finding list. Exported because the writer
 * truncates to exactly these numbers — a cap restated at the call site is a cap
 * that drifts, and the drift surfaces as an audit write that throws payload
 * validation in production. Same discipline as the GIT_PUSH_NO_DEPLOY caps.
 */
export const VIBE_AUDIT_SHIP_GATE_FINDING_LIST_CAP = 20;
export const VIBE_AUDIT_SHIP_GATE_PROBLEM_CAP = 500;

/**
 * Emitted when the ship gate read the repository at this deployment's own
 * `triggerSha` and refused to place it.
 *
 * A DISTINCT event type rather than `DEPLOYMENT_FAILED` with a reason string,
 * and that distinction is the point of the ticket. A deliberate refusal and a
 * broken build are different facts; conflating them makes "how often does the
 * gate fire, and on which artifact?" answerable only by string-matching a
 * human-readable message — the same category error as reading HEALTHY to mean
 * SERVED. The deployment's own `status` still goes terminal FAILED, because
 * that is what it is; this row is what says WHY, in a shape you can count.
 *
 * Nothing has been built when this fires — the gate runs at dispatch, between
 * the find and the claim, so a refusal contacts no builder and meters no build
 * minute; the job goes PENDING → FAILED with the deployment. A WARN finding rides
 * the claim instead, as `DEPLOYMENT_VERIFICATION_WARNED`.
 */
export const VibeAuditDeploymentVerificationRefusedPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_VERIFICATION_REFUSED"),
  vibeDeploymentId: z.string().uuid(),
  /// The commit the artifacts were read at — never the branch head. The gate's
  /// verdict is about the tree being shipped, and recording the ref it judged
  /// is what makes the row re-checkable months later.
  triggerSha: z.string().min(1).max(64),
  /// Which contract version produced this verdict, so an old row still says
  /// which rules applied rather than being read against today's set.
  contractVersion: z.number().int().positive(),
  /// Every artifact that was missing or red, with the reason. Bounded because
  /// an audit row must not be able to grow without limit; the contract declares
  /// far fewer artifacts than the cap, so truncation is a guard rather than a
  /// routine lossy path.
  findings: z
    .array(
      z.object({
        artifact: z.string().min(1).max(64),
        path: z.string().min(1).max(255),
        problem: z.string().min(1).max(VIBE_AUDIT_SHIP_GATE_PROBLEM_CAP)
      })
    )
    .min(1)
    .max(VIBE_AUDIT_SHIP_GATE_FINDING_LIST_CAP)
});
export type VibeAuditDeploymentVerificationRefusedPayload = z.infer<
  typeof VibeAuditDeploymentVerificationRefusedPayloadSchema
>;

/**
 * Emitted when the ship gate read the repository, found something, and the
 * deploy went ahead anyway — the app is in `WARN`.
 *
 * **This row is the entire point of `WARN`.** A refusal that does not refuse is
 * worthless if nothing records that it would have: without this event an app in
 * `WARN` is indistinguishable from one left `OFF`, and an organization has no
 * evidence on which to decide whether `ENFORCE` is safe to switch on. Counting
 * these rows against `DEPLOYMENT_VERIFICATION_REFUSED` is the measurement the
 * mode exists to produce.
 *
 * Written ALONGSIDE `DEPLOYMENT_BUILD_SUCCEEDED`, never instead of it, and in
 * the same transaction: the deploy really did advance, and dropping that row
 * would make "what shipped on deployment X?" unanswerable for exactly the
 * deploys someone is watching.
 *
 * The payload is the refusal's, verbatim — same fields, same caps — so the two
 * outcomes are countable side by side and a `WARN` row can be re-read months
 * later against the contract version that produced it. Deliberately NOT a
 * shared schema with a mode discriminator: these are separate `eventType`
 * literals in the union, and the audit feed narrows on that.
 */
export const VibeAuditDeploymentVerificationWarnedPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_VERIFICATION_WARNED"),
  vibeDeploymentId: z.string().uuid(),
  /// The commit the artifacts were read at — never the branch head.
  triggerSha: z.string().min(1).max(64),
  /// Which contract version produced this verdict, so an old row still says
  /// which rules applied rather than being read against today's set.
  contractVersion: z.number().int().positive(),
  /// Every artifact that was missing or red. Bounded by the same constants as
  /// the refusal, and `min(1)` for the same reason: a warning with no findings
  /// is a green gate, and writing a row for it would drown the signal.
  findings: z
    .array(
      z.object({
        artifact: z.string().min(1).max(64),
        path: z.string().min(1).max(255),
        problem: z.string().min(1).max(VIBE_AUDIT_SHIP_GATE_PROBLEM_CAP)
      })
    )
    .min(1)
    .max(VIBE_AUDIT_SHIP_GATE_FINDING_LIST_CAP)
});
export type VibeAuditDeploymentVerificationWarnedPayload = z.infer<
  typeof VibeAuditDeploymentVerificationWarnedPayloadSchema
>;

/**
 * Emitted when a caller deliberately shipped past the gate — `nexus vibe deploy
 * --skip-verification`, the server twin of the client hook's `NEXUS_GATE_ACK=1`.
 *
 * Written at TRIGGER time, when the human made the decision, and ONLY when the
 * app actually required verification: an override on an ungated app changes
 * nothing and writes nothing, so a count of these rows is a count of real
 * bypasses rather than of flag usage.
 *
 * `actorUserId` on the row is the overriding user and is the one audit event in
 * this family that is never a system actor. An unattributable override is
 * indistinguishable from having no gate at all, which is the exact bypass this
 * feature exists to close.
 */
export const VibeAuditDeploymentVerificationOverriddenPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_VERIFICATION_OVERRIDDEN"),
  vibeDeploymentId: z.string().uuid(),
  /// The commit that shipped ungated. The whole value of this row on a
  /// post-incident read is being able to say which tree skipped the check.
  triggerSha: z.string().min(1).max(64),
  /// The contract version the deploy would otherwise have been judged against.
  contractVersion: z.number().int().positive()
});
export type VibeAuditDeploymentVerificationOverriddenPayload = z.infer<
  typeof VibeAuditDeploymentVerificationOverriddenPayloadSchema
>;
